import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL;

const NewFeatures = ({ setAllFeatures, theFeatures, name, productId }) => {

  const location = useLocation();
  const { userInfo } = useSelector((state) => state.userLogin) || {}

  // Check if the URL contains 'edit' or 'create'
  const isEditMode = location.pathname.includes('edit');

  const [features, setFeatures] = useState([])
  const [newFeature, setNewFeature] = useState({
    heading: '',
    description: '',
    image: '',
  })
  const [updateData, setUpdateData] = useState('')
  // Upload problems used to fail silently (bare `return` / console.error only), so the
  // feature saved with image:'' and nobody could tell why. Surface it instead.
  const [imgError, setImgError] = useState('')
  // The upload is async but the file input fires onChange instantly. Without this,
  // clicking "Add Feature" before the request resolved saved the feature with
  // image:'' - the file reached disk but the path never reached the form state.
  const [uploading, setUploading] = useState(false)
  // The edit form renders at the top of this section, several screens above the
  // list, so clicking Edit looked like nothing happened. Scroll it into view.
  const editFormRef = useRef(null)

  const startEditFeature = (item) => {
    setImgError('')
    setUpdateData({
      id: item.id,
      heading: item.heading,
      description: item.description,
      image: item.image,
    })
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 0)
  }

  const uploadFeatureImage = async (e, mode = 'new') => {
    setImgError('')
    const file = e.target.files?.[0]
    if (!file) return
    if (!productId) {
      setImgError('Save the product once before adding feature images.')
      return
    }

    const formData = new FormData()
    formData.append('productObjectId', productId)
    formData.append('image', file)

    setUploading(true)
    try {
      // /api/upload is behind `protect, admin` - without this header it 401s and the
      // image is silently dropped. SkuManager already sends it; this did not.
      const { data } = await axios.post(`${apiUrl}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo?.token}`,
        },
      })
      const uploadedPath = String(data)
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)[0]

      if (!uploadedPath) {
        setImgError('Upload returned no file path. The feature will save without an image.')
        return
      }

      if (mode === 'update') {
        setUpdateData((prev) => ({ ...prev, image: uploadedPath }))
      } else {
        setNewFeature((prev) => ({ ...prev, image: uploadedPath }))
      }
    } catch (err) {
      const status = err?.response?.status
      setImgError(
        status === 401 || status === 403
          ? 'Image upload failed: not authorised. Log out and back in, then retry.'
          : `Image upload failed${status ? ` (${status})` : ''}. You can still save; add the image later via Update.`
      )
      console.error('Feature image upload failed', err)
    } finally {
      setUploading(false)
    }
  }

  // Let the admin drop an image without replacing it.
  const clearFeatureImage = (mode = 'new') => {
    setImgError('')
    if (mode === 'update') {
      setUpdateData((prev) => ({ ...prev, image: '' }))
    } else {
      setNewFeature((prev) => ({ ...prev, image: '' }))
    }
  }

  const imagePreview = (path, mode) => (
    <div className='d-flex align-items-center gap-2 mt-2'>
      <img
        src={`${apiUrl}/${String(path).replace(/^\/+/, '')}`}
        alt='feature'
        style={{ width: 56, height: 56, objectFit: 'cover', border: '1px solid #ddd' }}
      />
      <span className='text-muted small text-break'>{path}</span>
      <button
        type='button'
        className='btn btn-sm btn-outline-danger ms-2'
        onClick={() => clearFeatureImage(mode)}
      >
        Remove image
      </button>
    </div>
  )

  // add feature
  const addFeature = () => {
    if (newFeature) {
      let num = Date.now() + Math.random()
      let newEntry = {
        id: num,
        heading: newFeature.heading,
        description: newFeature.description,
        image: newFeature.image,
      }
      setFeatures([...features, newEntry])
      // SENDING BACK TO STORING DATA
      setAllFeatures([...features, newEntry])

      setNewFeature({ heading: '', description: '', image: '' })
    }
  }

  // get update feature
  const updateFeatureHandler = (e) => {
    setUpdateData({ ...updateData, [e.target.name]: e.target.value })
  }

  // update the feature
  const updateTheFeature = (e) => {
    let filterRecords = [...features].filter(
      (item) => item.id !== updateData.id
    )
    let updatedObject = [...filterRecords, updateData]
    setFeatures(updatedObject)
    // SENDING BACK TO STORING DATA
    setAllFeatures(updatedObject)
    setUpdateData('')
  }

  // cancel update
  const cancelFeature = (id) => {
    setUpdateData('')
  }

  // delete feature
  const deleteFeature = (id) => {
    let newFeatures = features.filter((feature) => feature.id !== id)
    setFeatures(newFeatures)
    // SENDING BACK TO STORING DATA
    setAllFeatures(newFeatures)
  }

  const createNewFeatureHandler = (e) => {
    setNewFeature({ ...newFeature, [e.target.name]: e.target.value })
  }

  useEffect(() => {
    // setAllFeatures(features)
    setFeatures(theFeatures || [])
    // SENDING BACK TO STORING DATA
    setAllFeatures(theFeatures || [])
  }, [theFeatures])

  return (
    <section className='pdpFeature__section'>
      {isEditMode && updateData != "" &&
        <div className='card update_card' ref={editFormRef}>
          <div className='card-header'>
            <label className='form-label'>
              Editing feature: <strong>{updateData?.heading || '(untitled)'}</strong>
            </label>
          </div>
          <div className='card-body'>
            <input
              type='text'
              className='form-control mb-2'
              name='heading'
              placeholder='Feature Heading'
              value={updateData && updateData.heading}
              onChange={(e) => updateFeatureHandler(e)}
            />
            <input
              type='text'
              className='form-control mb-2'
              name='description'
              placeholder='Feature Description'
              value={updateData && updateData.description}
              onChange={(e) => updateFeatureHandler(e)}
            />
            <div className='mb-2'>
              <input
                type='file'
                accept='image/*'
                className='form-control'
                onChange={(e) => uploadFeatureImage(e, 'update')}
              />
              {uploading && (
                <small className='d-block mt-1 text-primary'>Uploading image…</small>
              )}
              {updateData?.image ? imagePreview(updateData.image, 'update') : (
                <small className='d-block mt-1 text-muted'>No image</small>
              )}
              {imgError && (
                <div className='alert alert-warning py-1 px-2 mt-2 mb-0 small' role='alert'>
                  {imgError}
                </div>
              )}
            </div>
            <button
              type='button'
              onClick={updateTheFeature}
              disabled={uploading}
              className='btn btn-success mt-2 me-2'
            >
              {uploading ? 'Uploading…' : 'Update Feature'}
            </button>
            <button
              type='button'
              onClick={cancelFeature}
              className='btn btn-danger mt-2'
            >
              Cancel Update
            </button>
          </div>
        </div>
      }

      <div className='card mt-3 create_card'>
        <div className='card-header'>
          <label className='form-label'>Create new feature</label>
        </div>
        <div className='card-body'>
          <input
            type='text'
            className='form-control mb-2'
            name='heading'
            placeholder='Feature Heading'
            onChange={(e) => createNewFeatureHandler(e)}
          />
          <input
            type='text'
            className='form-control mb-2'
            name='description'
            placeholder='Feature Description'
            onChange={(e) => createNewFeatureHandler(e)}
          />
          <input
            type='file'
            accept='image/*'
            className='form-control mb-2'
            onChange={(e) => uploadFeatureImage(e, 'new')}
          />
          {uploading && (
            <small className='d-block mb-2 text-primary'>Uploading image…</small>
          )}
          {newFeature.image ? imagePreview(newFeature.image, 'new') : null}
          {imgError && (
            <div className='alert alert-warning py-1 px-2 mb-2 small' role='alert'>
              {imgError}
            </div>
          )}
          <button
            type='button'
            onClick={addFeature}
            disabled={uploading}
            className='btn btn-success mt-2'
          >
            {uploading ? 'Uploading…' : 'Add Feature'}
          </button>
        </div>
      </div>

      {features && features.length ? '' : 'No features added'}

      <div className='card mt-3'>
        <div className='card-header'>
          <div className='form-label'>List of features</div>
        </div>
        <div className='card-body card-body-list'>
          {features
            ?.sort((a, b) => (a.id > b.id ? 1 : -1))
            ?.map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <div className='card-content featureCard__content'>
                    <div className='card-heading'><b className='me-3'>Heading:</b>{item?.heading}</div>
                    <div className='card-desc'><b className='me-3'>Description</b>{item?.description}</div>
                    <div className='mt-2 d-flex align-items-center'>
                      <b className='me-3'>Image</b>
                      {item?.image ? (
                        <img
                          src={`${apiUrl}/${String(item.image).replace(/^\/+/, '')}`}
                          alt='feature'
                          style={{ width: 48, height: 48, objectFit: 'cover', border: '1px solid #ddd' }}
                        />
                      ) : (
                        <span className='text-danger small'>No image — click Edit to add one</span>
                      )}
                    </div>
                    <div className='cardBtn__box'>
                      <span
                        className='btn btn-primary'
                        onClick={() => deleteFeature(item.id)}
                      >
                        Delete
                      </span>
                      <span
                        className='btn btn-success'
                        onClick={() => startEditFeature(item)}
                      >
                        Edit
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              )
            })}
        </div>
      </div>
    </section >
  )
}

export default NewFeatures
